/*
 * hyperion-index:begin
 * ---
 * path: hyperion-cloud/web/src/platform/design-system/components/ui/DataTable.tsx
 * layer: web
 * area: components
 * kind: component
 * web_root: components
 * relpath: web/src/platform/design-system/components/ui/DataTable.tsx
 * filename: DataTable.tsx
 * extension: tsx
 * ---
 * hyperion-index:end
 */

/**
 * DataTable Component
 * Feature-rich data table with sorting, pagination, selection, and CSV export
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ArrowDownTrayIcon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './Table';
import { Checkbox } from './Checkbox';
import { Skeleton } from './Skeleton';
import { EmptyState } from './EmptyState';
import { Button } from './Button';
import { QuerySurfaceState, type QueryObservation } from './QuerySurfaceState';
import { rowActivationProps } from './useRowActivation';

export interface ColumnDef<T> {
  key: string;
  header: string;
  render?: (row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  emptyIcon?: React.ComponentType<{ className?: string }>;
  emptyMessage?: string;
  pageSize?: number;
  onRowClick?: (row: T) => void;
  selectable?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  exportFilename?: string;
  keyField?: string;
  /**
   * Controlled selection: the row keys (values of `keyField`) that are
   * selected. Keys that are not in `data` are ignored and pruned (the table
   * re-emits the selection without them through `onSelectionChange`).
   */
  selectedKeys?: ReadonlySet<string> | readonly string[];
  /**
   * Number of records the server holds when `data` is only the loaded subset
   * (e.g. the first `limit` rows). Shown in the footer so the loaded count is
   * not mistaken for the whole collection.
   */
  total?: number;
  /**
   * The query behind `data`. When it failed (or is unauthorized) and there are
   * no rows to show, the table renders a truthful unavailable/permission state
   * with retry instead of `emptyMessage`. Pass the useQuery result directly.
   */
  source?: Omit<QueryObservation<unknown>, 'data'>;
  /** Plural noun for state copy when `source` failed, e.g. "agents". */
  sourceLabel?: string;
}

type SortDir = 'asc' | 'desc';

function getNestedValue(obj: unknown, path: string): unknown {
  return path
    .split('.')
    .reduce((acc: unknown, key) => (acc as Record<string, unknown>)?.[key], obj);
}

export function DataTable<T extends Record<string, unknown>>({
  data,
  columns,
  isLoading,
  emptyIcon = TableCellsIcon,
  emptyMessage = 'No data found',
  pageSize: initialPageSize = 10,
  onRowClick,
  selectable,
  onSelectionChange,
  exportFilename,
  keyField = 'id',
  selectedKeys,
  total,
  source,
  sourceLabel = 'rows',
}: DataTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [internalSelected, setInternalSelected] = useState<ReadonlySet<string>>(() => new Set());
  const isControlled = selectedKeys !== undefined;
  const keyOf = useCallback((row: T) => String(row[keyField] ?? ''), [keyField]);

  // Only keys of rows that are actually in `data` count as selected: a row a
  // filter hid, or one the server dropped, can never be acted on.
  const dataKeys = useMemo(() => new Set(data.map(keyOf)), [data, keyOf]);
  const rawSelected = isControlled ? selectedKeys : internalSelected;
  const selected = useMemo(() => {
    const next = new Set<string>();
    for (const key of rawSelected ?? []) if (dataKeys.has(key)) next.add(key);
    return next;
  }, [rawSelected, dataKeys]);
  const rawSelectedCount = Array.isArray(rawSelected)
    ? rawSelected.length
    : (rawSelected as ReadonlySet<string> | undefined)?.size ?? 0;

  const onSelectionChangeRef = useRef(onSelectionChange);
  onSelectionChangeRef.current = onSelectionChange;
  const lastEmittedRef = useRef<T[]>([]);

  const commitSelection = useCallback(
    (next: Set<string>) => {
      if (!isControlled) setInternalSelected(next);
      const rows = data.filter((r) => next.has(keyOf(r)));
      lastEmittedRef.current = rows;
      onSelectionChangeRef.current?.(rows);
    },
    [data, isControlled, keyOf]
  );

  // When `data` changes, re-emit the selection as the current row objects,
  // minus rows that are gone. A caller holding the emitted rows (bulk actions)
  // never acts on a hidden row or on a stale copy of a row whose status moved.
  useEffect(() => {
    const rows = data.filter((r) => selected.has(keyOf(r)));
    const last = lastEmittedRef.current;
    const unchanged =
      rows.length === last.length && rows.every((row, index) => row === last[index]);
    if (unchanged && rawSelectedCount === selected.size) return;
    if (!isControlled && rawSelectedCount !== selected.size) setInternalSelected(selected);
    lastEmittedRef.current = rows;
    onSelectionChangeRef.current?.(rows);
  }, [data, selected, rawSelectedCount, isControlled, keyOf]);

  // Sorting
  const sorted = useMemo(() => {
    if (!sortKey) return data;
    return [...data].sort((a, b) => {
      const av = getNestedValue(a, sortKey);
      const bv = getNestedValue(b, sortKey);
      const cmp = String(av ?? '').localeCompare(String(bv ?? ''), undefined, { numeric: true });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [data, sortKey, sortDir]);

  // Pagination. Data can shrink under the current page (a filter applied on
  // page 3 leaves 3 rows): clamp so the body is never empty under a
  // "51–3 of 3" footer.
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, totalPages - 1);
  useEffect(() => {
    if (page !== currentPage) setPage(currentPage);
  }, [page, currentPage]);
  const pageData = useMemo(
    () => sorted.slice(currentPage * pageSize, (currentPage + 1) * pageSize),
    [sorted, currentPage, pageSize]
  );

  const handleSort = useCallback(
    (key: string) => {
      if (sortKey === key) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortKey(key);
        setSortDir('asc');
      }
      setPage(0);
    },
    [sortKey]
  );

  // Selection
  const toggleRow = useCallback(
    (row: T) => {
      const key = keyOf(row);
      const next = new Set(selected);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      commitSelection(next);
    },
    [selected, keyOf, commitSelection]
  );

  const pageKeys = useMemo(() => pageData.map(keyOf), [pageData, keyOf]);
  const selectedOnPage = pageKeys.filter((key) => selected.has(key)).length;
  const allOnPageSelected = pageKeys.length > 0 && selectedOnPage === pageKeys.length;
  const someOnPageSelected = selectedOnPage > 0 && !allOnPageSelected;

  const toggleAll = useCallback(() => {
    const next = new Set(selected);
    if (allOnPageSelected) for (const key of pageKeys) next.delete(key);
    else for (const key of pageKeys) next.add(key);
    commitSelection(next);
  }, [selected, allOnPageSelected, pageKeys, commitSelection]);

  // CSV export
  const exportCsv = useCallback(() => {
    if (!exportFilename) return;
    const header = columns.map((c) => c.header).join(',');
    const rows = sorted.map((row) =>
      columns
        .map((c) => {
          const val = getNestedValue(row, c.key);
          const str = String(val ?? '');
          return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
        })
        .join(',')
    );
    const blob = new Blob([header + '\n' + rows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportFilename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [columns, sorted, exportFilename]);

  // Loading skeleton
  if (isLoading) {
    return (
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead key={col.key} style={col.width ? { width: col.width } : undefined}>
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {Array.from({ length: 5 }).map((_, i) => (
            <TableRow key={i}>
              {columns.map((col) => (
                <TableCell key={col.key}>
                  <Skeleton className="h-4 w-3/4" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  // A failed source is never presented as an empty table.
  if (source && source.status !== 'success' && data.length === 0) {
    return (
      <QuerySurfaceState query={{ ...source, data: undefined }} label={sourceLabel}>
        {() => null}
      </QuerySurfaceState>
    );
  }

  // Empty state
  if (data.length === 0) {
    return <EmptyState stateKind="verified-empty" icon={emptyIcon} title={emptyMessage} />;
  }

  return (
    <div>
      {/* Toolbar */}
      {exportFilename && (
        <div className="flex justify-end mb-2">
          <Button variant="ghost" size="sm" onClick={exportCsv}>
            <ArrowDownTrayIcon className="h-4 w-4 mr-1.5" />
            Export CSV
          </Button>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {selectable && (
              <TableHead style={{ width: '40px' }}>
                <Checkbox
                  checked={allOnPageSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someOnPageSelected;
                  }}
                  onCheckedChange={toggleAll}
                  aria-checked={someOnPageSelected ? 'mixed' : allOnPageSelected}
                  aria-label="Select all rows on this page"
                />
              </TableHead>
            )}
            {columns.map((col) => {
              const isSorted = col.sortable && sortKey === col.key;
              const ariaSort = !col.sortable
                ? undefined
                : isSorted
                  ? sortDir === 'asc'
                    ? 'ascending'
                    : 'descending'
                  : 'none';
              return (
                <TableHead
                  key={col.key}
                  style={col.width ? { width: col.width } : undefined}
                  aria-sort={ariaSort}
                >
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className="inline-flex items-center gap-1 select-none rounded font-inherit hover:text-[rgb(var(--color-text-primary))] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-ring"
                    >
                      {col.header}
                      {isSorted ? (
                        sortDir === 'asc' ? (
                          <ChevronUpIcon className="h-3 w-3" aria-hidden="true" />
                        ) : (
                          <ChevronDownIcon className="h-3 w-3" aria-hidden="true" />
                        )
                      ) : (
                        <ChevronUpDownIcon className="h-3 w-3 opacity-40" aria-hidden="true" />
                      )}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1">{col.header}</span>
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageData.map((row, i) => {
            const rowKey = String(row[keyField] ?? i);
            const isSelected = selected.has(rowKey);
            const activation = onRowClick
              ? rowActivationProps(() => onRowClick(row))
              : {};
            return (
              <TableRow
                key={rowKey}
                data-state={isSelected ? 'selected' : undefined}
                {...activation}
              >
                {selectable && (
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleRow(row)}
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      aria-label={`Select row ${rowKey}`}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell key={col.key}>
                    {col.render ? col.render(row) : String(getNestedValue(row, col.key) ?? '')}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination */}
      <div className="flex items-center justify-between px-2 py-3 text-sm text-[rgb(var(--color-text-secondary))]">
        <div className="flex items-center gap-2">
          <span>Rows per page:</span>
          <select
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(0);
            }}
            aria-label="Rows per page"
            className="rounded border border-[rgb(var(--color-border))] bg-[rgb(var(--color-bg-secondary))] px-2 py-0.5 text-sm"
          >
            {[10, 25, 50].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-3">
          <span>
            {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, sorted.length)}{' '}
            of {sorted.length}
            {total !== undefined && total > sorted.length && <> loaded ({total} total)</>}
          </span>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage === 0}
              onClick={() => setPage(currentPage - 1)}
            >
              Prev
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={currentPage >= totalPages - 1}
              onClick={() => setPage(currentPage + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
